import React, { FC, useState } from 'react';

import { useQuery } from '@apollo/client';

import { GET_DETAILED_PATHWAY_TABLE_ANALYTIC } from '../../../../graphql/analytics';
import {
  DateRangeFilter,
  Maybe,
  PATHWAY_STATUSES,
  Query,
  QueryGetUsersProgressesByPathwayArgs,
  Scalars,
} from '../../../../types';
import { useAuth, useTablePagination } from '../../../../utils/hooks';
import { ANALYTICS_ROWS_PER_PAGE, UsersProgress } from '../../const';
import { handleExportDetailedData } from '../../helpers';
import { UsersProgressTable } from '../UsersProgressTable';

interface PathwayUsersTableProps {
  title?: string | null;
  description?: string;
  goBack: () => void;
  total?: number | null;
  userIds: Array<Maybe<Scalars['Int']>>;
  date?: Maybe<DateRangeFilter>;
  pathwayId?: string;
}

const PathwayUsersTable: FC<PathwayUsersTableProps> = ({
  title,
  description,
  goBack,
  total,
  userIds,
  date,
  pathwayId,
}) => {
  const { page, rowsPerPage, handleChangeRowsPerPage, handleChangePage } = useTablePagination({
    perPageKey: ANALYTICS_ROWS_PER_PAGE,
  });

  const [progressData, setProgressData] = useState<UsersProgress[]>([]);

  const { getAccessTokenSilently } = useAuth();

  const { loading } = useQuery<
    Pick<Query, 'GetUsersProgressesByPathway'>,
    QueryGetUsersProgressesByPathwayArgs
  >(GET_DETAILED_PATHWAY_TABLE_ANALYTIC, {
    variables: {
      filter: {
        user_ids: userIds,
        date: date,
        pathway_id: pathwayId || '',
        pagination_option: {
          page: page + 1,
          count_items_on_page: rowsPerPage,
        },
      },
    },
    fetchPolicy: 'network-only',
    skip: !pathwayId,
    onCompleted: (data) => {
      const progressOnVideo = data?.GetUsersProgressesByPathway || [];

      const res: UsersProgress[] = progressOnVideo.map((item) => {
        return {
          id: item?.user?.id,
          userName: item?.user?.name,
          completed: item?.status === PATHWAY_STATUSES.COMPLETED,
          status: item?.status === PATHWAY_STATUSES.NOT_WATCHED ? 'started' : item?.status || '',
          progress: item?.progress ? Math.round(item.progress) : 0,
        };
      });

      setProgressData(res);
    },
    onError: () => {
      setProgressData([]);
    },
  });

  const handleExportPathwayData = async () => {
    await handleExportDetailedData(
      `${process.env.REACT_APP_HTTP_API_URL}/csv/pathways/user-stats.csv`,
      JSON.stringify({
        filter: {
          user_ids: userIds,
          date: date,
          pathway_id: pathwayId,
        },
      }),
      title,
      getAccessTokenSilently,
    );
  };

  return (
    <UsersProgressTable
      title={title}
      description={description}
      goBack={goBack}
      loading={loading}
      page={page}
      rowsPerPage={rowsPerPage}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      total={total}
      data={progressData}
      handleExportData={handleExportPathwayData}
      name="detailedPathwaysTable"
    />
  );
};

export default PathwayUsersTable;
